/*
    This file is part of Universal Sequences.
    Universal Sequences is free software: you can redistribute it and/or modif
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Universal Sequences is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import Tone from 'tone';
import Effects from 'Effects.js';
import ConvolverReverbEffect from 'effects/ConvolverReverbEffect.js';
import EffectsChain from 'effects/EffectsChain.js';
import DelayEffect from 'effects/Delay.js';
import {getEffect} from 'effects/EffectsList.js';
import {setEffectParameterValues} from 'actions/index.js';
import ParameterContext from 'effects/ParameterContext.js';

const MUTE_VOLUME = -120;

const NAMES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

class Sends {
    // trackVolume -> master
    // trackVolume -> trackSendVolume -> Send
    constructor(isMaster, channelMixer, sequencerEngine) {
        this.sends = [];
        this.channelMixer = channelMixer;
        this.sequencerEngine = sequencerEngine;
        
        this.masterBus = new Send("Master Bus", undefined, undefined, undefined, sequencerEngine);
        var reverbSend = new Send("A", this.masterBus, channelMixer, 0, sequencerEngine);
        var reverb = new ConvolverReverbEffect(sequencerEngine);
        reverb.enable(true);
        reverb.setWet(1);
        reverbSend.addEffect(reverb);

        var delaySend = new Send("B", this.masterBus, channelMixer, 1, sequencerEngine);
        var echo = new DelayEffect(sequencerEngine);
        echo.enable(true);
        echo.setWet(1);
        delaySend.addEffect(echo);
        this.sends.push(reverbSend);
        this.sends.push(delaySend);

        reverbSend.setSends(this);
        delaySend.setSends(this);
    }

    createNewSend(pad) {
        let send = new Send(
            NAMES[this.sends.length],
            this.masterBus,
            this.channelMixer,
            this.sends.length,
            this.sequencerEngine,
            pad);

        send.setSends(this);
        this.sends.push(send);

        // connect every track to this
        for (let i in this.sequencerEngine.subSequences) {
            let sub = this.sequencerEngine.subSequences[i];
            for (let j in sub.tracks) {
                sub.tracks[j].newSend(send);
            }
        }
        return send;
    }

    dispose() {
        this.sends.forEach(s => s.dispose());
        this.masterBus.dispose();
        this.sends = undefined;
    }

    deleteSends(sends) {
        this.sends = this.sends.filter(
            s => !sends.includes(s));
        sends.map(s =>
                 s.dispose());
    }

    getSends() {
        return this.sends;
    }

    getJson() {
        return this.sends.map(
            send => send.getJson());
    }

    async loadFromJson(json, moduleNumber) {
        for (let i=0; i < json.length; i++) {
            let sendJson = json[i];
            if (moduleNumber !== undefined &&
                sendJson.pad === undefined) {
                // we only add sends that have pads in
                // module-load mode
                continue;
            }
            let c = i;
            if (sendJson.pad || i >= this.sends.length) {
                // first create
                this.createNewSend();
                c = this.sends.length - 1;
            }
            await this.sends[c].fromJson(sendJson);
        }
    }

    copyFrom(sends) {
        for (let i in sends.sends) {
            let effects = sends.sends[i].effects.map(x => x.getEffectCopy(this.sequencerEngine, false));
            if (!this.sends[i]) {
                this.createNewSend();
            }
            this.sends[i].replaceEffects(effects);
        }
    }
}

class Send {
    constructor(name, masterBus, channelMixer, sendNumber, sequencerEngine, pad) {
        // list of effects in the sends chain
        this.name = name;
        this.sequencerEngine = sequencerEngine;
        this.sendNumber = sendNumber;
        this.inputVolume = new Tone.Volume(0);
        this.outputVolume = new Tone.Volume(0);
        this.muteableNode = new Tone.Volume(0);
        this.muteableNode.connect(this.outputVolume);
        this.pad = pad;
        this.tracks = [];
        this.muted = false;

        
        this.effectsChain = new EffectsChain(
            this.inputVolume, this.muteableNode,
            new ParameterContext().forSend(this),
            sequencerEngine);

        if (masterBus === undefined) {
            this.inputVolume.connect(this.outputVolume);
        } else {
            this.outputs = channelMixer.connect(this.outputVolume);
        }

        this.sendNodes = [];
        this.channelMixer = channelMixer;
    }

    get outputNode() {
        return this.outputVolume;
    }

    dispose() {
        this.inputVolume.dispose();
        this.outputVolume.dispose();
        this.muteableNode.dispose();

        this.effectsChain.dispose();

        this.effectsChain = undefined;
        this.inputVolume = undefined;
        this.outputVolume = undefined;
        this.muteableNode = undefined;
        this.effectsChain = undefined;
    }
   

    // creates a new send on the track
    newSend(send) {
        var sendNode = new Tone.Volume(-100);
        this.outputVolume.connect(sendNode);
        send.connectToTrack(sendNode);
        this.sendNodes.push(sendNode);
    }

    getSendNodes() {
        return this.sendNodes;
    }

    setSends(sends) {
        this.sends = sends;
    }

    moveEffectTo(effectFrom, effectTo) {
        this.effectsChain.moveEffectTo(effectFrom, effectTo);
    }

    deleteEffect(effect) {
        this.effectsChain.deleteEffect(effect);
    }

    mute(muted) {
        this.muted = muted;
        if (muted) {
            this.muteableNode.volume.value = MUTE_VOLUME;
        } else {
            this.muteableNode.volume.value = 0;
        }
    }

    updateEffects() {
        this.effectsChain.updateEffects();
    }

    getVolume() {
        return this.outputVolume.volume.value;
    }

    setVolume(x) {
        this.outputVolume.volume.value = x;
    }

    connectToTrack(trackSendNode,  disconnect) {
        disconnect = disconnect || false;
        if (disconnect) {
            // disconnect first
            trackSendNode.disconnect(this.inputVolume);
        }
        // then connect
        trackSendNode.connect(this.inputVolume);
    }

    getEffects() {
        return this.effectsChain.effects;
    }

    getName() {
        return this.name;
    }

    addEffect(effect) {
        this.effectsChain.addEffect(effect);
        this.effectsChain.linkEffectParameters();
    }

    getLastEffect() {
        return this.effectsChain.effects.length == 0 ? this.inputVolume : this.effectsChain.effects[this.effectsChain.effects.length - 1];
    }

    getJson() {
        return {
            pad: this.pad ? this.pad.id : undefined,
            volume: this.getVolume(),
            effectsChain: this.effectsChain.getJson(),
            outputs: this.outputs,
        };
    }

    replaceEffects(effects) {
        this.effectsChain.clear();
        effects.map(effect => this.effectsChain.addEffect(effect));
    }

    get effects() {
        return this.effectsChain.effects;
    }

    async fromJson(json) {
        this.setVolume(json.volume);
       let effects = [];

        this.effectsChain.effects.forEach(e => e.dispose());
        this.effectsChain.effects = [];
        if (json.effectsChain) {
            effects = json.effectsChain;
        } else {
            effects = json.effects;
        }

        this.effectsJson = effects;
        if (json.outputs) {
            let [left, right] = json.outputs;
            if (this.channelMixer.switchChannelOutput(
                this.outputNode, 
                left,
                right)) {
                this.outputs = [left, right];
            }
 
        }

        if (json.pad) {
            let padId = json.pad;
            this.padId = json.pad;
       }
    }

    hookupPads() {
        if (this.pad) {
            // we've already loaded this previosuly
            return true;
        }
        if (!this.padId) {
            this.loadEffectsJson();
            return true;
        }
       
        // find the pad that this Send is responsible for (clue:
        // it is the effects)
        let _pad = undefined;
        for (let sub of this.sequencerEngine.subSequences) {
            let pad = sub.pads.find(x => x.id === this.padId);
            if (pad) {
                this.pad = pad;
               _pad = pad;
                break;
            }
        }
        if (_pad) {
            this.sequencerEngine.switchToSubSequence(
                _pad.subSequence.id);
        } 
        this.loadEffectsJson();
        if (_pad) {
            this.pad.effectRack = this.effects[0];
            this.pad.send = this;
            this.pad.syncTracksToEffects();
            this.pad.updateEffectRedux();
        }
        return _pad !== undefined;
    }

    loadEffectsJson() {
        if (this._loaded) {
            return;
        }
        let effects = this.effectsJson;
        let bulk = {};
        for (var i in effects) {
            let ret = getEffect(
                effects[i], undefined, this.sequencerEngine, undefined,
                this.pad && new ParameterContext().forSubSequence(this.pad.subSequence));
            this.effectsChain.addEffect(ret.effect);
            bulk = {
                ... bulk,
                ... ret.bulk
            };
        }
        this.sequencerEngine.store.dispatch(setEffectParameterValues(bulk));
        this._loaded = true;

    }
}

export default Sends;
